import { useEffect, useMemo, useRef, useState } from 'react';
import type { FocusEvent, ReactNode } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Field from './Field';
import { borderLabel, useLabelPlacement } from './labelPlacement';
import { describedBy, inputAriaLabel, inputAttrProps, isInvalid, useFieldIds, type BaseFieldProps } from './fieldUtils';
import './form.css';

/**
 * Dropdown / lookup on MUI Autocomplete, with AppSelectBox's props so a caller
 * can move across by changing the import.
 *
 * Behaviour:
 * - Typing filters (contains, case-insensitive, on `searchExpr` or the display
 *   text) and underlines the match. It never picks or highlights anything for
 *   you: the value changes only on a click, Enter on an option you moved to
 *   with the arrow keys or the mouse, or the clear button.
 * - Leaving the field without a pick puts the text back to the current value.
 * - `searchEnabled={false}` makes it a plain picker: no typing.
 *
 * onValueChanged fires only for those user actions, never when `value` or
 * `dataSource` change. A value set before its list has loaded simply shows
 * once the list arrives.
 */
export interface SelectFieldProps<T extends object = Record<string, unknown>> extends BaseFieldProps {
  /** The items. null / undefined (still loading) shows an empty list. */
  dataSource: readonly T[] | null | undefined;
  /** Field of each item shown as its text. */
  displayExpr: string;
  /** Field of each item used as the value. */
  valueExpr: string;
  /** The current value (an item's `valueExpr` field), or null. */
  value: unknown;
  /** The new value and its item; null when cleared. User changes only. */
  onValueChanged: (value: unknown, item: T | null) => void;
  /** Clear button (shown on hover and focus). Default true, as AppSelectBox. */
  showClearButton?: boolean;
  /** Typing to filter. Default true. */
  searchEnabled?: boolean;
  /** Field(s) searched; default `displayExpr`. */
  searchExpr?: string | string[];
  /** Max list height in px. Default 300. */
  maxDropdownHeight?: number;
  /** Custom option content (AppSelectBox `itemRender`). Default: text with the match underlined. */
  itemRender?: (item: T) => ReactNode;
  /** Draws the selected item inside the field in place of its plain text,
   *  e.g. a diet as its coloured chip. Return null to show the plain text.
   *  The input keeps the item's text (it is the accessible value, and what
   *  the user edits to search); the node shows while the box still holds the
   *  picked item's text and gives way as soon as the user types. The field
   *  itself, its outline and its label are never painted. The node is
   *  centred in the box and gets at most --app-select-value-max-h of height
   *  (clear of a border label); the item's text is the field's tooltip. */
  renderValue?: (item: T) => ReactNode;
  /** Render at most 100 matches, with a line saying how many more there are
   *  (AppSelectBox `paginate`, for lists in the thousands). */
  paginate?: boolean;
  /** Focus and open as soon as it mounts (an editor swapped into a cell). */
  autoOpen?: boolean;
  /** Called after the list closes, picked or not. */
  onPopupClosed?: () => void;
  /** Shown when nothing matches. */
  noDataText?: string;
}

const PAGE = 100;

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // Keys often arrive as "12" from one API and 12 from another.
  return a != null && b != null && typeof a !== 'object' && typeof b !== 'object' && String(a) === String(b);
}

function Highlighted({ text, search }: { text: string; search: string }) {
  const idx = search ? text.toLowerCase().indexOf(search.toLowerCase()) : -1;
  if (idx < 0) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <span className="app-select-match">{text.slice(idx, idx + search.length)}</span>
      {text.slice(idx + search.length)}
    </span>
  );
}

export default function SelectField<T extends object = Record<string, unknown>>(props: SelectFieldProps<T>) {
  const {
    dataSource, displayExpr, valueExpr, value, onValueChanged, label, ariaLabel, placeholder, required, disabled, readOnly,
    helperText, errorText, width, height, className, inputAttr, showClearButton = true, searchEnabled = true, searchExpr,
    maxDropdownHeight = 300, itemRender, renderValue, paginate = false, autoOpen = false, onPopupClosed, noDataText, labelPlacement,
  } = props;
  const ids = useFieldIds(inputAttr);
  const placement = useLabelPlacement(labelPlacement);
  const outlineLabel = borderLabel(placement, label, required);
  const items = dataSource ?? (EMPTY as readonly T[]);
  const field = (item: T, name: string) => (item as Record<string, unknown>)[name];
  const textOf = (item: T) => String(field(item, displayExpr) ?? '');

  const selected = useMemo(
    () => (value == null ? null : items.find((i) => same(field(i, valueExpr), value)) ?? null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, valueExpr, value],
  );

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(autoOpen);
  const inputRef = useRef<HTMLInputElement>(null);
  // autoOpen: an editor swapped into a cell takes focus with its list open.
  useEffect(() => {
    if (autoOpen) inputRef.current?.focus();
  }, [autoOpen]);

  // After a pick or clear, the box shows what the caller's `value` says, not
  // what was clicked: a caller that refuses the change, or resets the box
  // after each pick (an "add" list bound to null), gets its own text back
  // instead of the refused pick left sitting in the input.
  const syncAfterPick = useRef(false);
  useEffect(() => {
    if (!syncAfterPick.current) return;
    syncAfterPick.current = false;
    const text = selected ? textOf(selected) : '';
    if (inputValue !== text) setInputValue(text);
  });

  // The search term: none while the box still shows the picked item's text.
  const term = !searchEnabled || (selected && inputValue === textOf(selected)) ? '' : inputValue.trim();
  const searchFields = searchExpr == null ? [displayExpr] : Array.isArray(searchExpr) ? searchExpr : [searchExpr];
  const matches = useMemo(() => {
    if (!term) return items;
    const t = term.toLowerCase();
    return items.filter((i) => searchFields.some((f) => String(field(i, f) ?? '').toLowerCase().includes(t)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, term, searchFields.join('|')]);
  const shown = paginate && matches.length > PAGE ? matches.slice(0, PAGE) : matches;
  const more = matches.length - shown.length;

  const invalid = isInvalid(props);
  // The picked item drawn by the caller, while the box still shows its text.
  const valueNode = renderValue && selected && inputValue === textOf(selected) ? renderValue(selected) : null;

  return (
    <Field
      inputId={ids.id}
      label={label}
      required={required}
      helperText={helperText}
      helperId={ids.helperId}
      errorText={errorText}
      errorId={ids.errorId}
      error={invalid}
      disabled={disabled}
      width={width}
      height={height}
      labelPlacement={placement}
      className={[readOnly && 'app-field--readonly', !searchEnabled && 'app-select--nosearch', valueNode != null && 'app-select--value-node', className].filter(Boolean).join(' ') || undefined}
    >
      <Autocomplete<T, false, boolean, false>
        id={ids.id}
        options={items as T[]}
        value={selected}
        onChange={(e, item, reason) => {
          // MUI clears the value as soon as the text is emptied. Backspacing to
          // retype is not a decision; only the clear button clears, and leaving
          // an empty box puts the current value's text back.
          if (reason === 'clear' && (e?.type === 'change' || e?.type === 'input')) return;
          syncAfterPick.current = true;
          onValueChanged(item ? field(item, valueExpr) : null, item);
        }}
        inputValue={inputValue}
        onInputChange={(_e, v) => setInputValue(v)}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => { setOpen(false); onPopupClosed?.(); }}
        filterOptions={() => shown as T[]}
        getOptionLabel={textOf}
        getOptionKey={(o) => String(field(o, valueExpr))}
        isOptionEqualToValue={(a, b) => same(field(a, valueExpr), field(b, valueExpr))}
        // No auto-pick: nothing is highlighted until the arrow keys or the
        // mouse move to an option, and leaving never commits a highlight.
        autoHighlight={false}
        autoSelect={false}
        clearOnBlur
        disableClearable={!showClearButton}
        disabled={disabled}
        readOnly={readOnly}
        size="small"
        fullWidth
        noOptionsText={noDataText ?? (term ? 'No matches' : 'No items')}
        slotProps={{
          listbox: {
            style: { maxHeight: maxDropdownHeight },
            ...(more > 0 ? { 'data-more': `${more} more. Keep typing to narrow the list.`, className: 'app-select-listbox--more' } : {}),
          } as object,
        }}
        renderOption={(optProps, option) => {
          const { key, ...rest } = optProps as typeof optProps & { key: string };
          return (
            <li key={key} {...rest}>
              {itemRender ? itemRender(option) : <Highlighted text={textOf(option)} search={term} />}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={outlineLabel}
            InputLabelProps={outlineLabel ? { ...params.InputLabelProps, shrink: true } : params.InputLabelProps}
            // No label on the outline: no notch either (an empty notch is a gap in the top border).
            InputProps={{
              ...params.InputProps,
              notched: !!outlineLabel,
              // A drawn value (a diet chip) can be cut short with an ellipsis:
              // its full text shows on hover. On the box, not the input, so
              // it is not read out a second time as the input's description.
              title: valueNode != null && selected ? textOf(selected) : undefined,
              // Over the input, not in place of it: the input still holds the
              // text, so the value is read out and the user can type over it.
              startAdornment: valueNode != null
                ? <span className="app-select-value" aria-hidden="true">{valueNode}</span>
                : params.InputProps.startAdornment,
            }}
            placeholder={placeholder}
            error={invalid}
            inputRef={inputRef}
            inputProps={{
              ...params.inputProps,
              // Focus selects the picked item's text, however the box got focus, so
              // typing replaces it rather than adding to it (Lynbrook 10-28: a dialog
              // that focuses the box left the caret after "ENTREE: SPECIAL", and the
              // next letter searched for "ENTREE: SPECIAL T"). MUI's own selectOnFocus
              // runs on a click only.
              onFocus: (e: FocusEvent<HTMLInputElement>) => {
                (params.inputProps as { onFocus?: (ev: FocusEvent<HTMLInputElement>) => void }).onFocus?.(e);
                const el = e.currentTarget;
                if (!searchEnabled || readOnly || !selected) return;
                setTimeout(() => {
                  if (document.activeElement === el && el.value !== '' && el.value === textOf(selected)) el.select();
                }, 0);
              },
              ...inputAttrProps(inputAttr),
              readOnly: !searchEnabled || readOnly || undefined,
              'aria-label': inputAriaLabel(label, required, ariaLabel),
              'aria-required': required || undefined,
              'aria-invalid': invalid || undefined,
              'aria-describedby': describedBy(ids, { helperText, errorText }),
            }}
          />
        )}
      />
    </Field>
  );
}

const EMPTY: readonly never[] = [];
